import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, retry, throwError, timeout, timer } from 'rxjs';

/**
 * Stops a hung request from leaving the UI waiting forever, and smooths over
 * transient failures on idempotent reads.
 *
 * - Every request gets a timeout (20s by default).
 * - Writes that legitimately run long (project submit to SoftOne, PDF
 *   generation, order creation, file uploads) get a much longer one.
 * - Only GET requests are retried (twice, 1s apart). A 4xx is a definitive
 *   answer, so it is rethrown immediately rather than retried.
 */
@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  private static readonly DEFAULT_TIMEOUT_MS = 20000;
  private static readonly LONG_TIMEOUT_MS = 120000;

  /** Non-GET endpoints that may legitimately take longer than the default. */
  private static readonly LONG_RUNNING_PATHS = [
    '/submit',
    '/generate-pdf',
    '/setOrder',
    '/layer_images',
    '/images/logo',
    '/assignedMaterials',
    '/background_images',
    '/background-images',
    '/layer_entities',
    '/final_product_layers',
  ];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const timeoutMs = this.isLongRunning(request)
      ? TimeoutInterceptor.LONG_TIMEOUT_MS
      : TimeoutInterceptor.DEFAULT_TIMEOUT_MS;

    const call$ = next.handle(request).pipe(timeout(timeoutMs));

    if (request.method !== 'GET') {
      return call$;
    }

    return call$.pipe(
      retry({
        count: 2,
        delay: (error: unknown) => {
          const status = (error as HttpErrorResponse)?.status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            return throwError(() => error);
          }
          return timer(1000);
        },
      })
    );
  }

  private isLongRunning(request: HttpRequest<unknown>): boolean {
    if (request.method === 'GET') { return false; }
    if (typeof FormData !== 'undefined' && request.body instanceof FormData) { return true; }
    return TimeoutInterceptor.LONG_RUNNING_PATHS.some(path => request.url.includes(path));
  }
}
